/**
 * Authorized campaign history API for saved session notes and generated summaries.
 *
 * WHO: `CampaignHistory.tsx` reads and explicitly submits notes for the current authenticated
 *       campaign participant; the backend derives identity and checks live membership.
 * WHAT: GET pages curated campaign history; POST appends a note with a caller-stable idempotency ID.
 * WHERE: /api/events/campaigns/{campaign_id}/history and /api/events/sessions/{session_id}/notes,
 *        using the same authorizedFetch transport and structured ApiError policy as SessionService.
 * WHEN: Each request accepts AbortSignal so route, campaign, account, or component changes can
 *        cancel work. A retry must reuse the same entry_id and exact trimmed text.
 * WHY: Notes are explicit persisted records, distinct from unsupported realtime chat. The client
 *      validates response scope and shape, never sends actor identity, and never invents success.
 *
 * See shared/docs/Prompts/004_Campaign_Character_Session_Core_Flows_TODO_PRD.md (004.8).
 */
import { authorizedFetch } from './authTransport';
import { ApiError, apiErrorFromNetworkFailure, apiErrorFromResponse } from './ApiError';
import { getApiBaseUrl } from '../../utils/environment';

export type CampaignHistoryEntryType = 'session_note' | 'session_summary';
export interface CampaignHistoryEntry {
  entry_id: string;
  session_id: string;
  type: CampaignHistoryEntryType;
  text: string;
  author: string;
  created_at: string;
}
export interface CampaignHistoryPage {
  entries: CampaignHistoryEntry[];
  next_before_session_id: string | null;
}
export interface CampaignHistoryRequestOptions { signal?: AbortSignal; }

const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function invalidResponse(body: unknown, status: number): ApiError {
  return new ApiError('Invalid campaign history response', status, 'unknown', body);
}

function parseEntry(value: unknown, status: number): CampaignHistoryEntry {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalidResponse(value, status);
  const entry = value as Record<string, unknown>;
  // This endpoint deliberately returns a compact six-field public projection. Rejecting unknown
  // keys prevents internal actor identifiers from silently crossing into UI state before review.
  const expectedFields = ['author', 'created_at', 'entry_id', 'session_id', 'text', 'type'];
  if (Object.keys(entry).length !== expectedFields.length || Object.keys(entry).some((key) => !expectedFields.includes(key))) {
    throw invalidResponse(value, status);
  }
  const maxTextLength = entry.type === 'session_summary' ? 5000 : 4000;
  if (typeof entry.entry_id !== 'string' || !entry.entry_id ||
      typeof entry.session_id !== 'string' || !entry.session_id ||
      (entry.type !== 'session_note' && entry.type !== 'session_summary') ||
      typeof entry.text !== 'string' || (entry.type === 'session_note' && entry.text.trim().length === 0) ||
      Array.from(entry.text).length > maxTextLength || typeof entry.author !== 'string' || !entry.author.trim() ||
      typeof entry.created_at !== 'string' || !Number.isFinite(Date.parse(entry.created_at)) ||
      (entry.type === 'session_note' && !CANONICAL_UUID.test(entry.entry_id)) ||
      (entry.type === 'session_summary' && (entry.entry_id !== `summary:${entry.session_id}` || entry.author !== 'Session summary'))) {
    throw invalidResponse(value, status);
  }
  return { entry_id: entry.entry_id as string, session_id: entry.session_id as string, type: entry.type as CampaignHistoryEntryType, text: entry.text as string, author: entry.author as string, created_at: entry.created_at as string };
}

export class CampaignHistoryService {
  private readonly apiBaseUrl = getApiBaseUrl();

  async getCampaignHistory(
    campaignId: string,
    beforeSessionId?: string | null,
    options?: CampaignHistoryRequestOptions
  ): Promise<CampaignHistoryPage> {
    if (!campaignId.trim()) throw new Error('Campaign ID is required to load history.');
    const query = beforeSessionId ? `?before_session_id=${encodeURIComponent(beforeSessionId)}` : '';
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/events/campaigns/${encodeURIComponent(campaignId)}/history${query}`,
        { method: 'GET', signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to load campaign history');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to load campaign history');
    const body: unknown = await response.json();
    if (!body || typeof body !== 'object' || Array.isArray(body)) throw invalidResponse(body, response.status);
    const page = body as Record<string, unknown>;
    if (!Array.isArray(page.entries) || page.entries.length > 505 ||
        !(page.next_before_session_id === null || (typeof page.next_before_session_id === 'string' && page.next_before_session_id.length > 0))) {
      throw invalidResponse(body, response.status);
    }
    return { entries: page.entries.map((entry) => parseEntry(entry, response.status)), next_before_session_id: page.next_before_session_id as string | null };
  }

  async saveSessionNote(
    sessionId: string,
    entryId: string,
    text: string,
    options?: CampaignHistoryRequestOptions
  ): Promise<CampaignHistoryEntry> {
    const trimmedText = text.trim();
    if (!sessionId.trim()) throw new Error('Session ID is required to save a note.');
    if (!CANONICAL_UUID.test(entryId)) throw new Error('A stable UUID is required to save a note.');
    if (!trimmedText || Array.from(trimmedText).length > 4000) throw new Error('Notes must contain 1 to 4000 characters.');
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/events/sessions/${encodeURIComponent(sessionId)}/notes`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entry_id: entryId, text: trimmedText }), signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to save session note');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to save session note');
    const entry = parseEntry(await response.json(), response.status);
    if (entry.session_id !== sessionId || entry.entry_id !== entryId || entry.type !== 'session_note' || entry.text !== trimmedText) {
      throw invalidResponse(entry, response.status);
    }
    return entry;
  }
}

export const campaignHistoryService = new CampaignHistoryService();
